import { FC, useEffect, useMemo, useState } from 'react';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import ButtonBase from '@material-ui/core/ButtonBase';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Collapse from '@material-ui/core/Collapse';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import Check from '@material-ui/icons/Check';
import Close from '@material-ui/icons/Close';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import exerciseQueuesApi, { exerciseQueuesApiKeys } from 'api/rest/exerciseQueues/exerciseQueues';
import type { ExerciseQueue, ExerciseQueueItem } from 'api/rest/exerciseQueues/exerciseQueues.types';
import exercisesApi from 'api/rest/exercises/exercises';
import type { Exercise } from 'api/rest/exercises/exercises.types';
import { ExerciseFileTypes } from 'api/rest/exercises/exercises.types';
import { useUserLogContext } from 'context/UserLogContext/UserLogContext';
import type { Subject } from 'types/Subject';
import type { Uuid } from 'types/Uuid';

import OnGoLayout from '../OnGoLayout';
import FormErrorBanner from '../Initialize/FormErrorBanner';
import { resolveQueueErrorMessage } from '../Initialize/resolveQueueErrorMessage';
import { ON_GO_COLORS } from '../onGoColors';
import { getQueueItemStatusLabel } from './queueItemStatus';

const useStyles = makeStyles(theme => ({
  topRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  headline: {
    fontWeight: 700,
    lineHeight: 1.2,
  },
  scoreGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    flexShrink: 0,
  },
  scoreValue: {
    fontWeight: 700,
    lineHeight: 1,
  },
  scoreSub: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.25),
  },
  statsCard: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[100],
    marginBottom: theme.spacing(2.5),
  },
  statCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.25),
  },
  statValue: {
    fontWeight: 700,
  },
  statValueCorrect: {
    fontWeight: 700,
    color: ON_GO_COLORS.correct,
  },
  statValueIncorrect: {
    fontWeight: 700,
    color: ON_GO_COLORS.incorrect,
  },
  statLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    letterSpacing: 0.2,
    textTransform: 'uppercase',
  },
  detailsHeading: {
    fontWeight: 600,
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  detailsList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  detailsListItem: {
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
  },
  itemHeader: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 2.5),
    textAlign: 'left',
  },
  itemTitle: {
    fontWeight: 600,
    minWidth: 0,
  },
  itemHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    flexShrink: 0,
    color: theme.palette.text.secondary,
  },
  statusChip: {
    height: 24,
    borderWidth: 1,
    borderStyle: 'solid',
    backgroundColor: 'transparent',
    '& .MuiChip-label': {
      fontSize: '0.7rem',
      fontWeight: 600,
      paddingLeft: theme.spacing(1.25),
      paddingRight: theme.spacing(2.25),
      letterSpacing: 0.2,
      marginTop: theme.spacing(0.35),
    },
    '& .MuiChip-icon': {
      marginLeft: theme.spacing(1.75),
      // marginRight: theme.spacing(-0.25),
      fontSize: '0.85rem',
    },
  },
  statusChipCorrect: {
    color: ON_GO_COLORS.correct,
    borderColor: ON_GO_COLORS.correct,
    '& .MuiChip-icon': {
      color: ON_GO_COLORS.correct,
    },
  },
  statusChipIncorrect: {
    color: ON_GO_COLORS.incorrect,
    borderColor: ON_GO_COLORS.incorrect,
    '& .MuiChip-icon': {
      color: ON_GO_COLORS.incorrect,
    },
  },
  statusChipSkipped: {
    color: theme.palette.text.secondary,
    borderColor: theme.palette.grey[400],
  },
  itemBody: {
    padding: theme.spacing(0, 1.5, 1.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.grey[200]}`,
    paddingTop: theme.spacing(1.5),
  },
  itemBodySection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  itemBodySectionLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: 0.2,
    textTransform: 'uppercase',
  },
  answerValue: {
    fontWeight: 600,
  },
  answerValueCorrect: {
    fontWeight: 700,
    color: ON_GO_COLORS.correct,
  },
  answerValueIncorrect: {
    fontWeight: 700,
    color: ON_GO_COLORS.incorrect,
  },
  itemImage: {
    display: 'block',
    width: '100%',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.common.white,
  },
  itemEmpty: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
  inlineLoader: {
    display: 'flex',
    justifyContent: 'flex-start',
    padding: theme.spacing(0.5, 0),
  },
}));

const mediaUrl = process.env.REACT_APP_MEDIA_URL || '';

const renderQueueItemStatusChip = (item: ExerciseQueueItem, classes: ReturnType<typeof useStyles>) => {
  const label = getQueueItemStatusLabel(item);

  if (item.correct === true) {
    return <Chip className={`${classes.statusChip} ${classes.statusChipCorrect}`} icon={<Check />} label={label} size='small' />;
  }
  if (item.correct === false) {
    return <Chip className={`${classes.statusChip} ${classes.statusChipIncorrect}`} icon={<Close />} label={label} size='small' />;
  }
  return <Chip className={`${classes.statusChip} ${classes.statusChipSkipped}`} label={label} size='small' />;
};

/** Returns the labels of the options whose ids match the queue item's `correctAnswerIds`. */
const resolveCorrectAnswerLabels = (item: ExerciseQueueItem, exercise: Exercise | undefined): string[] => {
  if (!exercise) {
    return [];
  }
  if (item.correctAnswerIds.length === 0) {
    return [];
  }
  const correctIds = new Set(item.correctAnswerIds);
  return exercise.answers.filter(answer => correctIds.has(answer.id)).map(answer => answer.label);
};

export type OnGoSummaryProps = {
  queue: ExerciseQueue;
  subject: Subject;
};

const OnGoSummary: FC<OnGoSummaryProps> = ({ queue, subject }) => {
  const classes = useStyles();
  const queryClient = useQueryClient();
  const { logEvent } = useUserLogContext();
  const [expandedItemId, setExpandedItemId] = useState<number | null>(null);
  const [preloadedQuestionImages, setPreloadedQuestionImages] = useState<Record<string, boolean>>({});

  const { progress } = queue;
  const sortedItems = useMemo(() => [...queue.items].sort((a, b) => a.order - b.order), [queue.items]);
  const exerciseUuids = useMemo(() => sortedItems.map(item => item.exerciseUuid), [sortedItems]);

  const scorePercent = progress.total > 0 ? Math.round((progress.correct / progress.total) * 100) : null;

  const exerciseDetailsQuery = useQuery(
    ['onGoSummary', 'exerciseDetails', queue.uuid, exerciseUuids],
    async () => {
      const details = await Promise.all(
        exerciseUuids.map(async exerciseUuid => {
          const exercise = await exercisesApi.getSingle(exerciseUuid as Uuid)();
          return [exerciseUuid, exercise] as const;
        }),
      );
      return details.reduce<Record<string, Exercise>>((acc, [uuid, exercise]) => {
        acc[String(uuid)] = exercise;
        return acc;
      }, {});
    },
    {
      enabled: exerciseUuids.length > 0,
      staleTime: 1000 * 60 * 5,
    },
  );

  useEffect(() => {
    logEvent('on-go-complete', {
      subjectPk: subject.pk,
      subjectCode: subject.subjectCode,
      queueUuid: queue.uuid,
      correct: progress.correct,
      incorrect: progress.incorrect,
      total: progress.total,
    });
  }, [logEvent, progress.correct, progress.incorrect, progress.total, queue.uuid, subject.pk, subject.subjectCode]);

  const closeMutation = useMutation(exerciseQueuesApi.close(queue.uuid), {
    onSuccess: () => {
      queryClient.invalidateQueries(exerciseQueuesApiKeys.active(subject.pk));
    },
  });

  const onNewSession = () => {
    logEvent('on-go-new-session', {
      subjectPk: subject.pk,
      subjectCode: subject.subjectCode,
      queueUuid: queue.uuid,
    });
    closeMutation.mutate();
  };

  const toggleItemExpansion = (itemId: number) => {
    setExpandedItemId(prev => (prev === itemId ? null : itemId));
  };

  const closeError = closeMutation.isError ? resolveQueueErrorMessage(closeMutation.error) : null;
  const detailsError = exerciseDetailsQuery.isError ? resolveQueueErrorMessage(exerciseDetailsQuery.error) : null;

  useEffect(() => {
    if (!exerciseDetailsQuery.data) {
      return;
    }

    const questionImageUrls = Object.values(exerciseDetailsQuery.data)
      .flatMap(exercise => exercise.files[ExerciseFileTypes.question] || [])
      .map(src => `${mediaUrl}${src}`);

    questionImageUrls.forEach(url => {
      if (preloadedQuestionImages[url]) {
        return;
      }

      const img = new Image();
      img.onload = () => {
        setPreloadedQuestionImages(prev => ({ ...prev, [url]: true }));
      };
      img.onerror = () => {
        // Mark as ready even on error to avoid endless loader state.
        setPreloadedQuestionImages(prev => ({ ...prev, [url]: true }));
      };
      img.src = url;
    });
  }, [exerciseDetailsQuery.data, preloadedQuestionImages]);

  return (
    <OnGoLayout
      footer={
        <Button
          color='primary'
          disabled={closeMutation.isLoading}
          endIcon={closeMutation.isLoading ? <CircularProgress color='inherit' size={18} /> : undefined}
          fullWidth
          onClick={onNewSession}
          size='large'
          variant='contained'
        >
          {closeMutation.isLoading ? 'Zamykamy sesję…' : 'Nowa sesja'}
        </Button>
      }
    >
      <Box className={classes.topRow}>
        <Typography className={classes.headline} component='h1' variant='h5'>
          Sesja ukończona
        </Typography>
        {scorePercent != null ? (
          <Box className={classes.scoreGroup}>
            <Typography className={classes.scoreValue} component='span' variant='h4'>
              {scorePercent}%
            </Typography>
            <Typography className={classes.scoreSub} component='span' variant='caption'>
              {progress.correct} z {progress.total} poprawnych
            </Typography>
          </Box>
        ) : null}
      </Box>

      {closeError ? (
        <Box mb={2}>
          <FormErrorBanner message={closeError} />
        </Box>
      ) : null}

      <Box className={classes.statsCard} component='section'>
        <Box className={classes.statCell}>
          <Typography className={classes.statValue} variant='h6'>
            {progress.answered}/{progress.total}
          </Typography>
          <Typography className={classes.statLabel}>Rozwiązane</Typography>
        </Box>
        <Box className={classes.statCell}>
          <Typography className={classes.statValueCorrect} variant='h6'>
            {progress.correct}
          </Typography>
          <Typography className={classes.statLabel}>Poprawne</Typography>
        </Box>
        <Box className={classes.statCell}>
          <Typography className={classes.statValueIncorrect} variant='h6'>
            {progress.incorrect}
          </Typography>
          <Typography className={classes.statLabel}>Błędne</Typography>
        </Box>
      </Box>

      <Typography className={classes.detailsHeading} component='h2' variant='subtitle1'>
        Szczegóły sesji
      </Typography>

      {detailsError ? (
        <Box mb={2}>
          <FormErrorBanner message={detailsError} />
        </Box>
      ) : null}

      <Box aria-label='Szczegóły sesji' className={classes.detailsList} component='ul' role='list'>
        {sortedItems.map(item => {
          const exercise = exerciseDetailsQuery.data?.[String(item.exerciseUuid)];
          const questionImages = exercise?.files[ExerciseFileTypes.question] || [];
          const questionImageUrls = questionImages.map(src => `${mediaUrl}${src}`);
          const correctLabels = resolveCorrectAnswerLabels(item, exercise);
          const isExpanded = expandedItemId === item.id;
          const userAnswerClassName =
            item.correct === true
              ? classes.answerValueCorrect
              : item.correct === false
              ? classes.answerValueIncorrect
              : classes.answerValue;
          const isExerciseLoading = exerciseDetailsQuery.isLoading && !exercise;
          const areQuestionImagesReady = questionImageUrls.every(url => preloadedQuestionImages[url]);

          return (
            <Box key={item.id} className={classes.detailsListItem} component='li'>
              <ButtonBase
                aria-expanded={isExpanded}
                className={classes.itemHeader}
                component='div'
                onClick={() => toggleItemExpansion(item.id)}
              >
                <Typography className={classes.itemTitle} variant='body1'>
                  Zadanie {item.order + 1}
                </Typography>
                <Box className={classes.itemHeaderRight}>
                  {renderQueueItemStatusChip(item, classes)}
                  {isExpanded ? <ExpandLess fontSize='small' /> : <ExpandMore fontSize='small' />}
                </Box>
              </ButtonBase>

              <Collapse in={isExpanded} mountOnEnter unmountOnExit>
                <Box className={classes.itemBody}>
                  <Box className={classes.itemBodySection}>
                    <Typography className={classes.itemBodySectionLabel}>Treść zadania</Typography>
                    {isExerciseLoading || (questionImageUrls.length > 0 && !areQuestionImagesReady) ? (
                      <Box className={classes.inlineLoader}>
                        <CircularProgress size={18} />
                      </Box>
                    ) : questionImages.length > 0 ? (
                      questionImages.map(src => (
                        <img key={`${item.id}-q-${src}`} alt='' className={classes.itemImage} src={`${mediaUrl}${src}`} />
                      ))
                    ) : (
                      <Typography className={classes.itemEmpty} variant='body2'>
                        Brak treści zadania.
                      </Typography>
                    )}
                  </Box>

                  <Box className={classes.itemBodySection}>
                    <Typography className={classes.itemBodySectionLabel}>Twoja odpowiedź</Typography>
                    <Typography variant='body2'>
                      <span className={userAnswerClassName}>{item.submittedAnswer ?? 'Brak odpowiedzi'}</span>
                    </Typography>
                  </Box>

                  {item.correct !== true ? (
                    <Box className={classes.itemBodySection}>
                      <Typography className={classes.itemBodySectionLabel}>Poprawna odpowiedź</Typography>

                      {isExerciseLoading ? (
                        <Typography variant='body2'>
                          <span className={classes.answerValueCorrect}>-</span>
                        </Typography>
                      ) : correctLabels.length > 0 ? (
                        <Typography variant='body2'>
                          <span className={classes.answerValueCorrect}>{correctLabels.join(', ')}</span>
                        </Typography>
                      ) : (
                        <Typography className={classes.itemEmpty} variant='body2'>
                          Brak danych o poprawnej odpowiedzi.
                        </Typography>
                      )}
                    </Box>
                  ) : null}
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </OnGoLayout>
  );
};

export default OnGoSummary;
